import { InventoryWebsiteVisibilityComponent } from '../../../inventory-website-visibility.component';
import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RmsModule } from '@rms';
import { RmsPulldownRef } from '../../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';

type BoxCreationMode = 'bags' | 'boxes' | 'weight';

interface BoxAllocation {
  mode: BoxCreationMode;

  bagsAffected: number;
  sourceWeight: number;

  numberOfBoxes: number;
  boxWeight: number;
  boxedWeight: number;

  leftoverWeight: number;
  slackBags: number;
}
@Component({
  selector: 'app-create-22lb-box',
  standalone: true,
  imports: [InventoryWebsiteVisibilityComponent,
    CommonModule,
    RmsModule,
    FormsModule
  ],
  templateUrl: './create-22lb-box.component.html',
  styleUrl: './create-22lb-box.component.scss'
})
export class Create22lbBoxComponent {
  WebInvisible = true;

  Mode: BoxCreationMode = 'bags';

  creationModeOptions = [
    {
      label: 'Bags Used',
      value: 'bags'
    },
    {
      label: 'Number of Boxes',
      value: 'boxes'
    },
    {
      label: 'Weight Used',
      value: 'weight'
    }
  ];

  InventoryItem: any;

  BagsToUse: number | null = null;
  NumberOfBoxes: number | null = null;
  WeightToUse: number | null = null;

  SlackRemaining: boolean = false;

  readonly BoxWeight: number = 22;

  errorMessage: string | null = null;

  constructor(
    @Inject(RMS_PULLDOWN_DATA) private data: any,
    public ref: RmsPulldownRef
  ) {
    this.InventoryItem = data || {};
  }

  get availableQty(): number {
    return Number(this.InventoryItem?.AvailableQty ?? 0);
  }

  get netWeight(): number {
    return Number(this.InventoryItem?.NetWeight ?? 0);
  }

  get availableWeight(): number {
    return this.availableQty * this.netWeight;
  }

  get allocation(): BoxAllocation | null {

    if (this.netWeight <= 0) {
      return null;
    }

    /*
     * ----------------------------------------
     * MODE: BAGS USED
     * ----------------------------------------
     */
    if (this.Mode === 'bags') {

      const bagsAffected =
        Number(this.BagsToUse ?? 0);

      if (bagsAffected <= 0) {
        return null;
      }

      const sourceWeight =
        bagsAffected * this.netWeight;

      const numberOfBoxes =
        Math.floor(sourceWeight / this.BoxWeight);

      const boxedWeight =
        numberOfBoxes * this.BoxWeight;

      const leftoverWeight =
        this.round(sourceWeight - boxedWeight);

      return {
        mode: 'bags',

        bagsAffected,
        sourceWeight: this.round(sourceWeight),

        numberOfBoxes,
        boxWeight: this.BoxWeight,
        boxedWeight: this.round(boxedWeight),

        leftoverWeight,
        slackBags:
          this.SlackRemaining && leftoverWeight > 0
            ? 1
            : 0
      };
    }


    /*
     * ----------------------------------------
     * MODE: NUMBER OF BOXES
     * ----------------------------------------
     */
    if (this.Mode === 'boxes') {

      const numberOfBoxes =
        Number(this.NumberOfBoxes ?? 0);

      if (numberOfBoxes <= 0) {
        return null;
      }

      const boxedWeight =
        numberOfBoxes * this.BoxWeight;

      /*
       * Number of original bags that must be
       * opened to supply the requested weight.
       */
      const bagsAffected =
        Math.ceil(boxedWeight / this.netWeight);

      const sourceWeight =
        bagsAffected * this.netWeight;

      const leftoverWeight =
        this.round(sourceWeight - boxedWeight);

      return {
        mode: 'boxes',

        bagsAffected,
        sourceWeight: this.round(sourceWeight),

        numberOfBoxes,
        boxWeight: this.BoxWeight,
        boxedWeight: this.round(boxedWeight),

        leftoverWeight,
        slackBags:
          this.SlackRemaining && leftoverWeight > 0
            ? 1
            : 0
      };
    }


    /*
     * ----------------------------------------
     * MODE: WEIGHT USED
     * ----------------------------------------
     */
    const requestedWeight =
      Number(this.WeightToUse ?? 0);

    if (requestedWeight <= 0) {
      return null;
    }

    const numberOfBoxes =
      Math.floor(requestedWeight / this.BoxWeight);

    const boxedWeight =
      numberOfBoxes * this.BoxWeight;

    /*
     * These are the source bags physically
     * affected to obtain the requested weight.
     */
    const bagsAffected =
      Math.ceil(requestedWeight / this.netWeight);

    const leftoverWeight =
      this.round(requestedWeight - boxedWeight);

    return {
      mode: 'weight',

      bagsAffected,
      sourceWeight: this.round(requestedWeight),

      numberOfBoxes,
      boxWeight: this.BoxWeight,
      boxedWeight: this.round(boxedWeight),

      leftoverWeight,
      slackBags:
        this.SlackRemaining && leftoverWeight > 0
          ? 1
          : 0
    };
  }

  get canSave(): boolean {

    const allocation = this.allocation;

    if (!allocation) {
      return false;
    }

    if (allocation.numberOfBoxes <= 0) {
      return false;
    }

    if (allocation.bagsAffected > this.availableQty) {
      return false;
    }

    if (allocation.sourceWeight > this.availableWeight) {
      return false;
    }

    return true;
  }

  setMode(mode: BoxCreationMode): void {

    if (mode !== 'bags') {
      this.BagsToUse = null;
    }

    if (mode !== 'boxes') {
      this.NumberOfBoxes = null;
    }

    if (mode !== 'weight') {
      this.WeightToUse = null;
    }

    this.SlackRemaining = false;
    this.errorMessage = null;
  }

  save(): void {

    this.errorMessage = null;

    const allocation = this.allocation;

    if (!allocation) {
      this.errorMessage =
        'Enter a valid amount to process.';
      return;
    }

    if (allocation.numberOfBoxes <= 0) {
      this.errorMessage =
        'The selected amount does not contain enough weight to create a 22 lb box.';
        console.log(this.errorMessage)
      return;
    }

    if (allocation.bagsAffected > this.availableQty) {
      this.errorMessage =
        `Only ${this.availableQty} bag(s) are available on this ref#.`;
      console.log(this.errorMessage)
      return;
    }

    if (allocation.sourceWeight > this.availableWeight) {
      this.errorMessage =
        `Only ${this.availableWeight} lbs are available on this ref#.`;
      console.log(this.errorMessage)
      return;
    }

    this.ref.close({
      cancelled: false,
      WebInvisible: this.WebInvisible,

      Mode: allocation.mode,

      BagsAffected:
        allocation.bagsAffected,

      SourceWeight:
        allocation.sourceWeight,

      NumberOfBoxes:
        allocation.numberOfBoxes,

      BoxWeight:
        allocation.boxWeight,

      BoxedWeight:
        allocation.boxedWeight,

      LeftoverWeight:
        allocation.leftoverWeight,

      SlackRemaining:
        this.SlackRemaining,

      SlackBagsToCreate:
        allocation.slackBags,

      SlackWeight:
        this.SlackRemaining
          ? allocation.leftoverWeight
          : 0
    });
  }

  cancel(): void {
    this.ref.close({
      cancelled: true
    });
  }
  get validationMessage(): string | null {

    const allocation = this.allocation;

    /*
     * Don't show an error before the user
     * has actually entered anything.
     */
    if (!allocation) {

      const hasInput =
        (this.Mode === 'bags' && Number(this.BagsToUse ?? 0) > 0) ||
        (this.Mode === 'boxes' && Number(this.NumberOfBoxes ?? 0) > 0) ||
        (this.Mode === 'weight' && Number(this.WeightToUse ?? 0) > 0);

      if (hasInput) {
        return 'Unable to calculate the box allocation.';
      }

      return null;
    }

    if (allocation.numberOfBoxes <= 0) {
      return 'The selected amount does not contain enough weight to create a 22 lb box.';
    }

    if (allocation.bagsAffected > this.availableQty) {
      return `This requires ${allocation.bagsAffected} bag(s), but only ${this.availableQty} bag(s) are available on this ref#.`;
    }

    if (allocation.sourceWeight > this.availableWeight) {
      return `This requires ${allocation.sourceWeight} lbs, but only ${this.availableWeight} lbs are available on this ref#.`;
    }

    return null;
  }
  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
